import React, { useState, useCallback } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { createAuthenticatedAxiosInstance, createUser } from "../../api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import CustomNavbar from "./CustomNavbar";
import ConfirmationModal from "../general/ConfirmationModal";
import CancelContinue from "../general/CancelContinueModal";
import logo from "assets/logo.png";
import { validateEmail } from "../../utils/validators";
import { ERROR_MESSAGES } from "../../constants/userConstants";
import { useForm } from "../../hooks/useForm";
import "./CreateUser.scss";
import CustomSelect from "../general/CustomSelect";
import LoadingScreen from "../general/LoadingScreen";
import CustomBackButton from "components/general/CustomBackButton";

interface UserFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: string;
  [key: string]: string;
}

const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailHasReadyExists, setEmailHasReadyExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const roleOptions = [
    { value: "gerente", label: "Gerente" },
    { value: "asistente", label: "Asistente" },
  ];
  const [selectedRole, setSelectedRole] = useState<{
    value: string;
    label: string;
  } | null>(null);

  /**
   * Validation rules for the user form
   */
  const validationRules = {
    name: (value: string) => (value ? null : "El nombre es obligatorio"),
    email: (value: string) => validateEmail(value),
    password: (value: string) =>
      value.length >= 8
        ? null
        : "La contraseña debe tener al menos 8 caracteres",
    confirmPassword: (value: string, formData: UserFormData) =>
      value === formData.password ? null : "Las contraseñas no coinciden",
    role: (value: string) => (value ? null : "El rol es obligatorio"),
  };

  /**
   * Custom hook for form handling
   */
  const { formData, errors, handleChange, handleSubmit, validateField } =
    useForm<UserFormData>(
      {
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: "",
      },
      validationRules,
      createUserCallback
    );

  /**
   * Callback function to create a new user
   */
  async function createUserCallback() {
    try {
      setIsLoading(true);
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      const { status } = await createUser(axiosInstance, formData);
      if (status === 200 || status === 201) {
        setIsLoading(false);
        setShowSuccessModal(true);
      } else {
        setShowErrorModal(true);
      }
    } catch (error: any) {
      setIsLoading(false);
      setShowErrorModal(true);
      if (
        error?.response?.data.errors.email[0] ===
        "The email has already been taken."
      ) {
        setEmailHasReadyExists(true);
      }
      console.error(
        "Error creating user:",
        error?.response?.data.errors.email[0] || error
      );
    }
  }
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  /**
   * Handler for navigating back to the users dashboard
   */
  const handleBack = useCallback(
    () => navigate("/dashboard#usuarios"),
    [navigate]
  );

  /**
   * Handler for showing the cancel confirmation modal
   */
  const handleCancel = useCallback(() => setShowCancelModal(true), []);

  /**
   * Handler for confirming cancellation and navigating back to the users dashboard
   */
  const handleConfirmCancel = useCallback(
    () => navigate("/dashboard#usuarios"),
    [navigate]
  );

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={handleBack} />

      <Container className="create-user">
        <Row className="mb-3">
          <Col xs={12} className="text-center">
            <img src={logo} alt="Logo" className="logo" />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            <h1 style={{ fontWeight: "bold" }}>Creación de usuario</h1>
          </Col>
        </Row>
        {isLoading ? (
          <LoadingScreen loadingText="Cargando información del usuario" />
        ) : (
          <Row className="justify-content-center">
            <Col md={8} className="user-info-container">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre:</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Nombre completo"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Puesto:</Form.Label>
                  <CustomSelect
                    options={roleOptions}
                    value={selectedRole}
                    placeholder="Puesto"
                    onChange={(newValue) => {
                      setSelectedRole(newValue);
                      if (newValue) {
                        handleChange({
                          target: { name: "role", value: newValue.value },
                        } as React.ChangeEvent<HTMLSelectElement>);
                      }
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Correo electrónico:</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => validateField("email", formData.email)}
                    isInvalid={!!errors.email}
                    placeholder="Correo eleectrónico"
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Contraseña:</Form.Label>
                  <div className="password-input-container">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      style={{
                        paddingRight: "40px",
                        backgroundImage: "none",
                      }}
                      value={formData.password}
                      onChange={handleChange}
                      isInvalid={!!errors.password}
                      placeholder="Contraseña"
                      required
                    />
                    <Button
                      type="button"
                      className="password-toggle-btn"
                      onClick={togglePasswordVisibility}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                        color="black"
                      />
                    </Button>
                  </div>
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: errors.password ? "block" : "none" }}
                  >
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Confirmar contraseña:</Form.Label>
                  <div className="password-input-container">
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      style={{
                        paddingRight: "40px",
                        backgroundImage: "none",
                      }}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      isInvalid={!!errors.confirmPassword}
                      placeholder="Contraseña"
                      required
                    />
                    <Button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleConfirmPasswordVisibility}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEyeSlash : faEye}
                        color="black"
                      />
                    </Button>
                  </div>
                  <Form.Control.Feedback
                    type="invalid"
                    style={{
                      display: errors.confirmPassword ? "block" : "none",
                    }}
                  >
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                </Form.Group>
                <Row className="mt-4">
                  <Col xs={6}>
                    <Button variant="secondary" onClick={handleCancel}>
                      Cancelar
                    </Button>
                  </Col>
                  <Col xs={6} className="text-end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={Object.values(errors).some(
                        (error) => error !== null
                      )}
                    >
                      Crear Usuario
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        )}
      </Container>
      <CancelContinue
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
        title="Atención"
        message={ERROR_MESSAGES.cancelCreation}
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />
      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          navigate("/dashboard#usuarios");
        }}
        title="Confirmación"
        message={ERROR_MESSAGES.userCreated}
        icon={faCheckCircle}
        iconColor="text-success"
      />
      <ConfirmationModal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
          setEmailHasReadyExists(false);
        }}
        title="Error"
        message={
          emailHasReadyExists
            ? ERROR_MESSAGES.emailHasAlreadyUsed
            : ERROR_MESSAGES.createUserError
        }
        icon={faTimesCircle}
        iconColor="text-danger"
      />
    </>
  );
};

export default React.memo(CreateUser);
